import {Injectable, NgZone} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MessageBusService} from 'projects/shared-components/message-bus.service';
import {ServerTimeMessageDto} from 'projects/shared-components/shell-communication/shell-dto-protocol';
import {AuthTokenExpiredUIMessage} from 'projects/shared-components/ui-messages/auth-token-expired-ui-message.interface';
import {EventStreamConnectionChangedUIMessage} from 'projects/shared-components/ui-messages/event-stream-connection-changed-message-ui-message.interface';
import {LastQuoteCacheService} from 'projects/shared-components/last-quote-cache.service';
import {TradingInstrumentsService} from 'projects/shared-components/trading-instruments/trading-instruments-service.interface';
import {TradingInstrumentKind} from 'projects/shared-components/trading-instruments/trading-instrument-kind.enum';
import {QuoteDto} from 'projects/shared-components/shell-communication/dtos/quote-dto.class';
import {EventStreamServiceClient} from 'projects/shared-components/event-stream/event-stream-service-client.service';
import {
   HIDE_DEBOUNCE_MS,
   MIN_OVERLAY_VISIBLE_MS,
   PROBE_FAIL_LOG_EVERY_NTH,
   PROBE_INTERVAL_MS,
   QUOTE_FRESHNESS_TOLERANCE_MS,
   SERVER_TIME_TOLERANCE_MS,
   SessionSyncDiagnosticStats,
   SessionSyncState,
   STALE_OVERLAY_KICKOUT_MS,
   VISIBILITY_HEARTBEAT_EVERY_NTH,
   VISIBILITY_POLL_MS
} from './session-sync-overlay.constants';

// CR-20260508-001 — App-wide overlay shown immediately when the tab is hidden,
// kept visible after return until either:
//   1. both freshness probes pass (server-time + SPX/ES quote), OR
//   2. an existing kickout fires (AuthTokenExpiredUIMessage from any source).
//
// Designed as the SUCCESSOR to DriftDetectionService — owns its own
// ServerTimeMessageDto subscription so it has zero runtime dependency on
// drift. Drift is disabled via feature flag during the experiment.
//
// HALF-EXTRACTION (iteration 7) — overlay markup, CSS, and show/hide JS
// live in index.html + assets/session-sync-overlay.{css,js}, OUTSIDE
// Angular. This service drives the overlay via the vanilla JS hook
// `window.__etsSessionSyncOverlay.show()/.hide()`. Bypasses Angular CD,
// NgZone, OnPush, and the async pipe — DOM mutation is synchronous in
// the visibilitychange handler so the post-mutation paint state has the
// best chance of being captured in the browser's last cached frame
// before tab hide. Crypto exchanges (Binance, ByBit, OKX) use the same
// pattern.
//
// Visibility detection uses Option B (events + polling fallback): both
// inputs route into the same reducer, which reads document.hidden directly
// and only transitions on real change vs _wasActive. Diagnostic logs record
// which source detected each transition first, so we can settle the
// CR-20260424-003 question of whether visibilitychange events fire in the
// user's environment.

type DetectionSource = 'event:visibilitychange' | 'poll' | 'init';

interface EtsSessionSyncOverlayHook {
   show(): void;
   hide(): void;
   isVisible(): boolean;
}

function getOverlayHook(): EtsSessionSyncOverlayHook | undefined {
   return (typeof window !== 'undefined')
      ? (window as any).__etsSessionSyncOverlay
      : undefined;
}

@Injectable({providedIn: 'root'})
export class SessionSyncOverlayService {

   constructor(
      private readonly _messageBus: MessageBusService,
      private readonly _lastQuoteCache: LastQuoteCacheService,
      private readonly _tiService: TradingInstrumentsService,
      private readonly _ngZone: NgZone,
      private readonly _eventStreamServiceClient: EventStreamServiceClient,
   ) {
      // Side-effect-free per memory rule (feedback_di_side_effect_free_ctors).
      this._unsubscriber = new Subject<any>();
   }

   //
   //  HALF-EXTRACTION (iteration 7) — visibility is driven by the vanilla
   //  JS hook on window, not by an Angular BehaviorSubject. setSyncing()
   //  calls window.__etsSessionSyncOverlay.show()/.hide() directly. No
   //  zone re-entry required — vanilla DOM mutation is synchronous and
   //  doesn't depend on Angular CD.
   //
   //  Internal `_isSyncingFlag` mirrors the visual state for debug-hook
   //  queries and onKickout's "was the overlay actually shown" check.
   //

   private readonly _unsubscriber: Subject<any>;
   private _isSyncingFlag = false;

   private _started = false;
   private _state: SessionSyncState = 'IDLE';

   private _lastServerTimeMessage: ServerTimeMessageDto | undefined;
   // CR-20260508-001 — arrival timestamp (Date.now() at receipt) of the most
   // recent SPX/ES QuoteDto. Distinct from QuoteDto.timeStamp (backend
   // content time): content can be hours stale during after-hours while the
   // pipeline is fully alive. Arrival tracks pipeline liveness, which is
   // what "session in sync" actually means here.
   //
   // CR-20260509-001 — note: probe A uses CONTENT time of
   // ServerTimeMessageDto (not arrival time) because its purpose is
   // server-clock vs browser-clock drift detection. Arrival-based was
   // a misread iter 32; reverted because pause/resume protocol now
   // defers ServerTime during pause, so post-resume content age is
   // the meaningful signal.
   private _lastSpxOrEsQuoteArrivalMs: number = 0;
   private _lastSpxOrEsQuoteTicker: string = '';
   private _wasActive = true;

   // LEAK-1 — ESS-connection state gate. Probes may pass only if
   //   (a) ESS is currently connected AND
   //   (b) we've seen a fresh ResendCompleted (isConnected=true edge)
   //       since the most recent disconnect.
   // Prevents the case where a Resend-replayed stale-content quote
   // updates _lastSpxOrEsQuoteArrivalMs to "now" before the live stream
   // resumes, causing probes to pass on stale data.
   private _essConnected = false;
   private _needFreshResendSinceDisconnect = false;

   // Timers / listeners
   private _pollHandle: any = undefined;
   private _probeIntervalHandle: any = undefined;
   private _staleKickoutHandle: any = undefined;
   private _hideDebounceHandle: any = undefined;
   private _visibilityListener: (() => void) | undefined;

   // Diagnostics
   private _stats: SessionSyncDiagnosticStats = this.makeEmptyStats();
   private _hiddenAt = 0;
   private _visibleAt = 0;
   private _shownAt = 0;
   private _probeLoopStartedAt = 0;
   private _probeAFailLogTickCounter = 0;
   private _probeBFailLogTickCounter = 0;
   private _visibilityHeartbeatCounter = 0;
   private _initialNoServerTimeMsgLogged = false;
   private _vacuousProbeBLogged = false;

   //

   private setSyncing(value: boolean): void {
      if (this._isSyncingFlag === value) {
         return;
      }
      this._isSyncingFlag = value;
      const hook = getOverlayHook();
      if (!hook) {
         console.warn(
            '[SessionSyncOverlayService] window.__etsSessionSyncOverlay missing — '
            + 'overlay JS hook from index.html did not load. State change still '
            + 'tracked internally but no visual update.'
         );
         return;
      }
      if (value) {
         hook.show();
      } else {
         hook.hide();
      }
   }

   //

   start(): void {
      if (this._started) {
         console.info('[SessionSyncOverlayService] start() called but already running — no-op');
         return;
      }
      this._started = true;
      this._wasActive = this.computeIsActive();

      // LEAK-1 — seed ESS-connection state. If we start mid-session (typical
      // /app navigation), the worker already finished Welcome+Resend so
      // isConnected=true is accurate. If start runs before initial handshake,
      // subscribeEssConnection() picks up the first event.
      this._essConnected = this._eventStreamServiceClient.isConnected;
      this._needFreshResendSinceDisconnect = false;

      this.subscribeServerTime();
      this.subscribeQuotes();
      this.subscribeKickout();
      this.subscribeEssConnection();
      this.attachVisibilityListener();
      this.startPolling();
      this.attachDebugHook();

      // CR-20260509-001 — engage pause/resume gate. /app-only rule
      // centralized here: this service starts/stops together with
      // WebtraderHostComponent (the only mount point on /app per
      // wt-app-routing.module). Worker uses the gate to decide whether
      // to send Pause/Resume on visibility transitions.
      this._eventStreamServiceClient.setPauseResumeGate(true);

      console.info(
         `[SessionSyncOverlayService] Started (events+polling enabled, `
         + `probeIntervalMs=${PROBE_INTERVAL_MS}, visibilityPollMs=${VISIBILITY_POLL_MS}, `
         + `serverTimeToleranceMs=${SERVER_TIME_TOLERANCE_MS}, `
         + `quoteFreshnessToleranceMs=${QUOTE_FRESHNESS_TOLERANCE_MS}, `
         + `staleOverlayKickoutMs=${STALE_OVERLAY_KICKOUT_MS}, `
         + `initialActive=${this._wasActive})`
      );

      // If we somehow start while the tab is already hidden, treat that as a
      // hidden transition so the overlay reflects reality.
      if (!this._wasActive) {
         this.onHidden('init');
      }
   }

   stop(): void {
      if (!this._started) {
         return;
      }
      // CR-20260508-001 iter 21 — force overlay hidden on stop. Without
      // this, if the host component is destroyed (e.g. programmatic
      // logout, route navigation away from /app) while the overlay was
      // showing, the `is-visible` class would persist into /login or
      // /logout. The kickout subscription handles AuthTokenExpired-
      // driven flow, but direct route navigations don't fire that
      // message — stop() is the catch-all.
      this.setSyncing(false);

      // CR-20260509-001 — disengage pause/resume gate. Worker handles
      // in-flight cleanup: if currently paused (tab hidden), worker
      // emits one Resume to clear backend state before accepting the
      // gate flip. See ess-worker.ts PAUSE_RESUME_GATE handler.
      this._eventStreamServiceClient.setPauseResumeGate(false);

      this.detachVisibilityListener();
      this.stopPolling();
      this.stopProbeLoop();
      this.clearStaleKickout();
      this.clearHideDebounce();

      if (this._unsubscriber && !this._unsubscriber.closed) {
         this._unsubscriber.next();
         this._unsubscriber.complete();
      }
      this._started = false;
      console.info('[SessionSyncOverlayService] Stopped (listeners removed, timers cleared, overlay hidden)');
   }

   reset(): void {
      const prevState = this._state;
      this.stopProbeLoop();
      this.clearStaleKickout();
      this.clearHideDebounce();
      this._state = 'IDLE';
      this._lastServerTimeMessage = undefined;
      this._lastSpxOrEsQuoteArrivalMs = 0;
      this._lastSpxOrEsQuoteTicker = '';
      this._wasActive = true;
      this._hiddenAt = 0;
      this._visibleAt = 0;
      this._shownAt = 0;
      this._probeLoopStartedAt = 0;
      this._probeAFailLogTickCounter = 0;
      this._probeBFailLogTickCounter = 0;
      this._visibilityHeartbeatCounter = 0;
      this._initialNoServerTimeMsgLogged = false;
      this._vacuousProbeBLogged = false;
      this._firstServerTimeRawLogged = false;
      this._firstSpxOrEsQuoteRawLogged = false;
      this._stats = this.makeEmptyStats();
      this.setSyncing(false);
      console.info(
         `[SessionSyncOverlayService] reset — prevState=${prevState} → IDLE `
         + `(cleared _lastServerTimeMessage, _wasActive, timers, stats)`
      );
   }

   //
   //  Subscriptions
   //

   private _firstServerTimeRawLogged = false;

   private subscribeServerTime(): void {
      this._messageBus
         .of<ServerTimeMessageDto>('ServerTimeMessageDto')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(x => {
            this._lastServerTimeMessage = x.payload;
            if (!this._firstServerTimeRawLogged) {
               this._firstServerTimeRawLogged = true;
               // One-shot diagnostic: log raw wire shape AND full payload
               // structure so root-cause investigation of the missing
               // `time` field has full context.
               const raw: any = x.payload && x.payload.time;
               const ctor = raw && raw.constructor ? raw.constructor.name : typeof raw;
               console.info(
                  `[SessionSyncOverlayService] first ServerTimeMessageDto received `
                  + `(time.constructor=${ctor}, time.raw=${JSON.stringify(raw)}, `
                  + `parsedEpochMs=${SessionSyncOverlayService.toEpochMs(raw)}, `
                  + `fullPayload=${JSON.stringify(x.payload)})`
               );
            }
         });
   }

   private subscribeQuotes(): void {
      // CR-20260508-001 — track ARRIVAL TIME of SPX/ES QuoteDto messages.
      // Same topic as LastQuoteCacheService (multicast bus); we only stamp
      // the local arrival time, not the backend content time. See
      // _lastSpxOrEsQuoteArrivalMs comment for rationale.
      this._messageBus
         .of<QuoteDto[]>('QuoteDto')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(x => this.onQuotesArrived(x.payload));
   }

   private _firstSpxOrEsQuoteRawLogged = false;

   private onQuotesArrived(quotes: QuoteDto[]): void {
      if (!quotes || quotes.length === 0) {
         return;
      }
      for (const q of quotes) {
         if (this.isProbeBTicker(q.ticker)) {
            this._lastSpxOrEsQuoteArrivalMs = Date.now();
            this._lastSpxOrEsQuoteTicker = q.ticker;
            if (!this._firstSpxOrEsQuoteRawLogged) {
               this._firstSpxOrEsQuoteRawLogged = true;
               // One-shot diagnostic: log raw wire shape for QuoteDto.timeStamp
               // so we can verify timezone-suffix handling.
               const raw: any = q.timeStamp;
               const ctor = raw && raw.constructor ? raw.constructor.name : typeof raw;
               console.info(
                  `[SessionSyncOverlayService] first SPX/ES quote arrival `
                  + `(ticker=${q.ticker}, timeStamp.constructor=${ctor}, `
                  + `timeStamp.raw=${JSON.stringify(raw)}, `
                  + `parsedEpochMs=${SessionSyncOverlayService.toEpochMs(raw)})`
               );
            }
            return;
         }
      }
   }

   private subscribeKickout(): void {
      this._messageBus
         .of<AuthTokenExpiredUIMessage>('AuthTokenExpiredUIMessage')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(x => this.onKickout(x.payload));
   }

   // LEAK-1 — track ESS connection state so probes can gate on a fresh
   // ResendCompleted handshake. ess-worker publishes:
   //   isConnected=false  on socket_onclose / disconnect
   //   isConnected=true   on ResendCompletedMessage (post-Welcome+Resend)
   // → isConnected:true edge = "server-side catch-up complete".
   private subscribeEssConnection(): void {
      this._messageBus
         .of<EventStreamConnectionChangedUIMessage>('EventStreamConnectionChangedUIMessage')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(x => {
            const nowConnected = !!x.payload.isConnected;
            if (!nowConnected) {
               // Disconnect: latch — any subsequent reconnect must wait for
               // a fresh ResendCompleted before probes may pass.
               this._essConnected = false;
               this._needFreshResendSinceDisconnect = true;
               console.info(
                  `[SessionSyncOverlayService] ESS disconnected — `
                  + `probes gated until next ResendCompleted`
               );
            } else {
               this._essConnected = true;
               this._needFreshResendSinceDisconnect = false;
               console.info(
                  `[SessionSyncOverlayService] ESS reconnected (ResendCompleted) — `
                  + `probes ungated`
               );
            }
         });
   }

   //
   //  Visibility detection — Option B
   //

   private attachVisibilityListener(): void {
      // Run outside Angular zone — DOM events should not trigger change detection.
      this._ngZone.runOutsideAngular(() => {
         this._visibilityListener = () => {
            this._stats.visibilityEventsFired++;
            const hidden = (typeof document !== 'undefined') ? document.hidden : false;
            const hasFocus = (typeof document !== 'undefined' && typeof document.hasFocus === 'function')
               ? document.hasFocus()
               : true;
            console.info(
               `[SessionSyncOverlayService] visibility-event fired `
               + `(document.hidden=${hidden}, hasFocus=${hasFocus})`
            );
            this.onVisibilitySignal('event:visibilitychange');
         };
         document.addEventListener('visibilitychange', this._visibilityListener);
      });
   }

   private detachVisibilityListener(): void {
      if (this._visibilityListener) {
         document.removeEventListener('visibilitychange', this._visibilityListener);
         this._visibilityListener = undefined;
      }
   }

   private startPolling(): void {
      if (this._pollHandle !== undefined) {
         return;
      }
      this._ngZone.runOutsideAngular(() => {
         this._pollHandle = setInterval(() => this.pollTick(), VISIBILITY_POLL_MS);
      });
   }

   private stopPolling(): void {
      if (this._pollHandle !== undefined) {
         clearInterval(this._pollHandle);
         this._pollHandle = undefined;
      }
   }

   private pollTick(): void {
      this._stats.visibilityPollsFired++;
      this.onVisibilitySignal('poll');

      // Heartbeat: emit a one-line summary every Nth poll IF no transition
      // was observed (steady-state diagnostic).
      this._visibilityHeartbeatCounter++;
      if (this._visibilityHeartbeatCounter >= VISIBILITY_HEARTBEAT_EVERY_NTH) {
         this._visibilityHeartbeatCounter = 0;
         console.info(
            `[SessionSyncOverlayService] visibility-poll heartbeat `
            + `(events=${this._stats.visibilityEventsFired}, `
            + `polls=${this._stats.visibilityPollsFired}, `
            + `transitions=${this._stats.transitions}, `
            + `wonByEvent=${this._stats.firstDetectionWinsByEvent}, `
            + `wonByPoll=${this._stats.firstDetectionWinsByPoll}, `
            + `state=${this._state})`
         );
      }
   }

   private onVisibilitySignal(source: DetectionSource): void {
      const nowActive = this.computeIsActive();
      if (nowActive === this._wasActive) {
         return;  // no transition — silent no-op
      }
      // Real transition.
      this._wasActive = nowActive;
      this._stats.transitions++;
      // Reset heartbeat counter so the next steady-state summary aligns.
      this._visibilityHeartbeatCounter = 0;
      if (source === 'event:visibilitychange') {
         this._stats.firstDetectionWinsByEvent++;
      } else if (source === 'poll') {
         this._stats.firstDetectionWinsByPoll++;
      }
      if (nowActive) {
         this.onVisible(source);
      } else {
         this.onHidden(source);
      }
   }

   // CR-20260508-001 — activeness is "tab is visible to the user," NOT
   // "tab has keyboard focus." Page Visibility API: document.hidden=true
   // when the tab is completely hidden (background tab, minimized window).
   // document.hasFocus()=false alone (e.g. user clicked Notepad on top of
   // the visible browser window) does NOT mean the tab is hidden — the
   // browser keeps delivering messages at full rate, so no catch-up is
   // needed and the overlay must NOT appear.
   //
   // Earlier iterations AND-ed `!hidden && hasFocus` (inherited from
   // CR-20260424-003 drift-detection's "is user away" check), which
   // conflated "hidden" with "unfocused." Smoke discovered this:
   // clicking a small overlay window (Notepad) triggered the overlay.
   // Fixed by checking document.hidden only.
   private computeIsActive(): boolean {
      if (typeof document === 'undefined') {
         return true;
      }
      return !document.hidden;
   }

   //
   //  State transitions
   //

   private onHidden(source: DetectionSource): void {
      if (this._state === 'KICKED_OUT') {
         return;  // latched terminal — overlay stays hidden until reset()
      }
      const now = Date.now();
      this._hiddenAt = now;
      // CR-20260508-001 iter 24 — debounce overlay show. Rapid alt-tabs
      // (<HIDE_DEBOUNCE_MS) must not trigger any overlay. Aligns with
      // PAUSE_DEBOUNCE_MS in ess-worker: brief hides generate zero wire
      // traffic AND zero visual disruption. setSyncing(true) deferred to
      // the timer callback; state stays IDLE until then.
      this.clearHideDebounce();
      this._ngZone.runOutsideAngular(() => {
         this._hideDebounceHandle = setTimeout(() => {
            this._hideDebounceHandle = undefined;
            // Guard against late-fire after KICKED_OUT or state change.
            if (this._state !== 'IDLE') {
               return;
            }
            this._shownAt = Date.now();
            this._state = 'SYNCING';
            this.setSyncing(true);
            console.info(
               `[SessionSyncOverlayService] hide-debounce fired after `
               + `${HIDE_DEBOUNCE_MS}ms — overlay shown`
            );
         }, HIDE_DEBOUNCE_MS);
      });
      console.info(
         `[SessionSyncOverlayService] tab HIDDEN — overlay debounce armed `
         + `(firstDetectedBy=${source}, debounceMs=${HIDE_DEBOUNCE_MS})`
      );
   }

   private clearHideDebounce(): void {
      if (this._hideDebounceHandle !== undefined) {
         clearTimeout(this._hideDebounceHandle);
         this._hideDebounceHandle = undefined;
      }
   }

   private onVisible(source: DetectionSource): void {
      if (this._state === 'KICKED_OUT') {
         return;
      }
      const now = Date.now();
      this._visibleAt = now;
      const hiddenDurationMs = this._hiddenAt > 0 ? now - this._hiddenAt : 0;
      // LEAK-1 — decide based on wall-clock, not on whether the
      // setTimeout fired. Chrome background-tab throttling can defer
      // timers past HIDE_DEBOUNCE_MS; relying on _hideDebounceHandle
      // missed overlay-show on long-but-throttled hides. Always cancel
      // any pending timer, then gate purely on hiddenDurationMs.
      this.clearHideDebounce();
      if (hiddenDurationMs < HIDE_DEBOUNCE_MS) {
         console.info(
            `[SessionSyncOverlayService] tab VISIBLE — hide within debounce `
            + `(firstDetectedBy=${source}, hiddenDurationMs=${hiddenDurationMs}, `
            + `debounceMs=${HIDE_DEBOUNCE_MS}) — no overlay shown`
         );
         return;
      }
      // Hidden ≥ debounce: ensure overlay is visible even if the timer
      // never fired (browser-throttled). setSyncing is idempotent.
      if (!this._isSyncingFlag) {
         this._shownAt = now;
         this.setSyncing(true);
      }
      this._state = 'PROBING';
      this._probeLoopStartedAt = now;
      // CR-20260508-001 — re-anchor _shownAt to tab-return so
      // MIN_OVERLAY_VISIBLE_MS becomes "minimum visible after return"
      // rather than "minimum visible since hide began." Without this,
      // any hide longer than 750ms bypasses the anti-flicker / repaint-
      // mask floor entirely and probes can clear the overlay in 0ms
      // post-return — which is exactly the iteration-6 bug.
      this._shownAt = now;
      this._probeAFailLogTickCounter = 0;
      this._probeBFailLogTickCounter = 0;
      this._initialNoServerTimeMsgLogged = false;
      this._vacuousProbeBLogged = false;
      this._stats.probeAttempts = 0;

      console.info(
         `[SessionSyncOverlayService] tab VISIBLE — probe loop starting `
         + `(firstDetectedBy=${source}, latencyMs=0, hiddenDurationMs=${hiddenDurationMs})`
      );

      this.startProbeLoop();
      this.scheduleStaleKickout();
   }

   private onKickout(payload: AuthTokenExpiredUIMessage): void {
      if (this._state === 'KICKED_OUT') {
         return;
      }
      const overlayDurationMs = this._shownAt > 0 ? Date.now() - this._shownAt : 0;
      const wasShown = this._isSyncingFlag;
      this._state = 'KICKED_OUT';
      this.stopProbeLoop();
      this.clearStaleKickout();
      this.clearHideDebounce();
      this.setSyncing(false);
      if (wasShown) {
         console.warn(
            `[SessionSyncOverlayService] kickout terminator fired `
            + `(source=${payload?.source ?? 'unknown'}, overlayDurationMs=${overlayDurationMs}) `
            + `— overlay hidden, latch=KICKED_OUT`
         );
      } else {
         console.info(
            `[SessionSyncOverlayService] kickout received while overlay was hidden `
            + `(source=${payload?.source ?? 'unknown'}) — latch=KICKED_OUT`
         );
      }
   }

   //
   //  Probe loop
   //

   private startProbeLoop(): void {
      if (this._probeIntervalHandle !== undefined) {
         return;
      }
      this._ngZone.runOutsideAngular(() => {
         this._probeIntervalHandle = setInterval(() => this.probeTick(), PROBE_INTERVAL_MS);
      });
      // Also run a probe immediately so we don't wait the first interval.
      this.probeTick();
   }

   private stopProbeLoop(): void {
      if (this._probeIntervalHandle !== undefined) {
         clearInterval(this._probeIntervalHandle);
         this._probeIntervalHandle = undefined;
      }
   }

   private probeTick(): void {
      if (this._state !== 'PROBING') {
         return;
      }
      this._stats.probeAttempts++;
      const aPass = this.probeA();
      // CR-20260509-001 — probe B is unreliable (depends on SPX/ES quote
      // arrivals which can gap arbitrarily during slow markets, halts,
      // weekends, or empty-workspace sessions). Pause/resume protocol
      // unifies pipeline state so probe A (server clock content vs
      // browser clock) is sufficient as the sync gate. probeB() still
      // called for diagnostic logging only — its return value does NOT
      // gate sync-restore.
      this.probeB();

      // LEAK-1 — gate clearance on ESS connection state. Even if probe A
      // passes on a ServerTime delivered via Resend-replay, refuse to
      // clear until a fresh ResendCompleted handshake has been observed
      // since the most recent disconnect. Prevents showing stale data
      // that was simply replayed from server-side _unackedMessagesByNum.
      const connectionGated = !this._essConnected || this._needFreshResendSinceDisconnect;
      if (connectionGated) {
         if (aPass && this._probeAFailLogTickCounter++ % PROBE_FAIL_LOG_EVERY_NTH === 0) {
            console.info(
               `[SessionSyncOverlayService] probe gate — aPass=true but `
               + `essConnected=${this._essConnected}, `
               + `needFreshResend=${this._needFreshResendSinceDisconnect} — overlay kept`
            );
         }
         return;
      }

      if (aPass) {
         // Honor MIN_OVERLAY_VISIBLE_MS anti-flicker.
         const elapsed = Date.now() - this._shownAt;
         if (elapsed < MIN_OVERLAY_VISIBLE_MS) {
            // Re-arm: clear at the next probe tick, by which time the floor
            // will have elapsed. No log spam — we're about to clear cleanly.
            return;
         }
         this.onSyncRestored();
      }
   }

   private onSyncRestored(): void {
      const probeLoopDurationMs = Date.now() - this._probeLoopStartedAt;
      this._state = 'IDLE';
      this.stopProbeLoop();
      this.clearStaleKickout();
      this.setSyncing(false);
      console.info(
         `[SessionSyncOverlayService] SYNC-RESTORED — overlay hidden `
         + `(probeLoopDurationMs=${probeLoopDurationMs}, totalProbeAttempts=${this._stats.probeAttempts})`
      );
   }

   //
   //  Probes
   //

   // Normalize a wire-deserialized timestamp value (Date | ISO string |
   // epoch number) to epoch milliseconds. Returns NaN on junk input.
   //
   // CR-20260508-001 — diagnosed via two empirical logs:
   //   (1) localhost-1778231892863.log: QuoteDto.timeStamp arrives as ISO
   //       string despite type-declaring `timeStamp: Date`. Unary + on a
   //       non-numeric string returns NaN. Fixed by routing through Date().
   //   (2) localhost-1778232271604.log: freshness 10,801,370 ms ≈ exactly
   //       3 hours = the user's local-vs-UTC offset. Backend sends UTC time
   //       AS A STRING WITHOUT `Z` SUFFIX, so `new Date("2026-05-08T19:00:00")`
   //       parses as LOCAL, producing a tz-offset skew. Fix: detect missing
   //       tz suffix and append `Z` so JS interprets as UTC.
   private static toEpochMs(value: any): number {
      if (value === null || value === undefined) {
         return NaN;
      }
      if (typeof value === 'number') {
         return value;
      }
      if (value instanceof Date) {
         return value.getTime();
      }
      if (typeof value === 'string') {
         // ISO 8601 timezone suffix: `Z` or `+HH:MM`/`-HH:MM`/`+HHMM`/`-HHMM`.
         const hasTzSuffix = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
         const normalized = hasTzSuffix ? value : value + 'Z';
         return new Date(normalized).getTime();
      }
      // Last-resort coercion for unknown wire shapes.
      return new Date(value).getTime();
   }

   private probeA(): boolean {
      // CR-20260509-001 — probe A is the server-clock-vs-browser-clock
      // drift check. Compares ServerTimeMessageDto.time (content) to
      // Date.now(). Pause/resume protocol defers ServerTime delivery
      // during hide; on resume, server emits the freshest ServerTime
      // before resuming normal flow, so post-resume content time is
      // a real "is server clock current?" signal.
      if (!this._lastServerTimeMessage) {
         this._stats.probeAFailures++;
         if (!this._initialNoServerTimeMsgLogged) {
            this._initialNoServerTimeMsgLogged = true;
            console.info(
               `[SessionSyncOverlayService] probeA FAIL (no ServerTimeMessageDto received yet)`
            );
         }
         return false;
      }
      const tsMs = SessionSyncOverlayService.toEpochMs(this._lastServerTimeMessage.time);
      if (!Number.isFinite(tsMs)) {
         this._stats.probeAFailures++;
         this._probeAFailLogTickCounter++;
         if (this._probeAFailLogTickCounter >= PROBE_FAIL_LOG_EVERY_NTH) {
            this._probeAFailLogTickCounter = 0;
            console.info(
               `[SessionSyncOverlayService] probeA FAIL `
               + `(serverTimeGapMs=invalid; raw time=${JSON.stringify(this._lastServerTimeMessage.time)})`
            );
         }
         return false;
      }
      const gapMs = Date.now() - tsMs;
      if (gapMs <= SERVER_TIME_TOLERANCE_MS) {
         return true;
      }
      this._stats.probeAFailures++;
      this._probeAFailLogTickCounter++;
      if (this._probeAFailLogTickCounter >= PROBE_FAIL_LOG_EVERY_NTH) {
         this._probeAFailLogTickCounter = 0;
         console.info(
            `[SessionSyncOverlayService] probeA FAIL `
            + `(serverTimeGapMs=${gapMs}, threshold=${SERVER_TIME_TOLERANCE_MS})`
         );
      }
      return false;
   }

   private static readonly ES_FRONT_MONTH_REGEX = /^ES[HMUZ]\d$/;

   private isProbeBTicker(ticker: string): boolean {
      if (!ticker) {
         return false;
      }
      if (ticker === 'SPX') {
         return true;
      }
      if (!SessionSyncOverlayService.ES_FRONT_MONTH_REGEX.test(ticker)) {
         return false;
      }
      const ti = this._tiService.getInstrumentByTicker(ticker);
      return !!ti && ti.kind === TradingInstrumentKind.Futures;
   }

   // CR-20260508-001 — probe B detects whether the message-bus pipeline is
   // delivering SPX/ES QuoteDto traffic, NOT whether the backend content is
   // current. Discovered empirically (log localhost-1778232271604):
   // QuoteDto.timeStamp is the backend tick time, which is legitimately
   // hours stale during after-hours trading. Switched to ARRIVAL TIME
   // (Date.now() at receipt) — this captures the real intent of "session
   // in sync = data is flowing through the pipeline."
   //
   // Vacuous-pass logic preserved: if the user has no SPX/ES subscriptions
   // (cache contains zero matching tickers), probe B passes vacuously.
   private probeB(): boolean {
      const all = this._lastQuoteCache.getAllQuotes();
      let hasSpxOrEsSubscription = false;
      for (const q of all) {
         if (this.isProbeBTicker(q.ticker)) {
            hasSpxOrEsSubscription = true;
            break;
         }
      }
      if (!hasSpxOrEsSubscription) {
         if (!this._vacuousProbeBLogged) {
            this._vacuousProbeBLogged = true;
            console.info(
               `[SessionSyncOverlayService] probeB PASS (vacuous — no SPX/ES tickers in cache)`
            );
         }
         return true;
      }
      if (this._lastSpxOrEsQuoteArrivalMs === 0) {
         // Subscriptions exist but no quote has arrived yet — possible during
         // initial render before any tick. Throttled fail log.
         this._stats.probeBFailures++;
         this._probeBFailLogTickCounter++;
         if (this._probeBFailLogTickCounter >= PROBE_FAIL_LOG_EVERY_NTH) {
            this._probeBFailLogTickCounter = 0;
            console.info(
               `[SessionSyncOverlayService] probeB FAIL `
               + `(SPX/ES subscriptions present but no quote arrival observed yet, `
               + `threshold=${QUOTE_FRESHNESS_TOLERANCE_MS})`
            );
         }
         return false;
      }
      const arrivalAgeMs = Date.now() - this._lastSpxOrEsQuoteArrivalMs;
      if (arrivalAgeMs <= QUOTE_FRESHNESS_TOLERANCE_MS) {
         console.info(
            `[SessionSyncOverlayService] probeB PASS `
            + `(lastArrivedTicker=${this._lastSpxOrEsQuoteTicker}, `
            + `arrivalAgeMs=${arrivalAgeMs}, threshold=${QUOTE_FRESHNESS_TOLERANCE_MS})`
         );
         return true;
      }
      this._stats.probeBFailures++;
      this._probeBFailLogTickCounter++;
      if (this._probeBFailLogTickCounter >= PROBE_FAIL_LOG_EVERY_NTH) {
         this._probeBFailLogTickCounter = 0;
         console.info(
            `[SessionSyncOverlayService] probeB FAIL `
            + `(lastArrivedTicker=${this._lastSpxOrEsQuoteTicker}, `
            + `arrivalAgeMs=${arrivalAgeMs}, threshold=${QUOTE_FRESHNESS_TOLERANCE_MS})`
         );
      }
      return false;
   }

   //
   //  Stale-overlay force-logout
   //

   private scheduleStaleKickout(): void {
      this.clearStaleKickout();
      this._ngZone.runOutsideAngular(() => {
         this._staleKickoutHandle = setTimeout(() => this.fireStaleKickout(), STALE_OVERLAY_KICKOUT_MS);
      });
   }

   private clearStaleKickout(): void {
      if (this._staleKickoutHandle !== undefined) {
         clearTimeout(this._staleKickoutHandle);
         this._staleKickoutHandle = undefined;
      }
   }

   private fireStaleKickout(): void {
      if (this._state !== 'PROBING') {
         return;
      }
      console.warn(
         `[SessionSyncOverlayService] STALE-OVERLAY-KICKOUT — `
         + `${STALE_OVERLAY_KICKOUT_MS}ms elapsed without sync; `
         + `publishing AuthTokenExpiredUIMessage(source=SessionSyncStaleTimeout)`
      );
      // Publishing AuthTokenExpiredUIMessage triggers our own kickout
      // subscription which will latch us into KICKED_OUT and hide the overlay.
      this._messageBus.publish<AuthTokenExpiredUIMessage>({
         topic: 'AuthTokenExpiredUIMessage',
         payload: {
            doNotShowLogoutScreen: false,
            source: 'SessionSyncStaleTimeout'
         }
      });
   }

   //
   //  Diagnostics
   //

   private makeEmptyStats(): SessionSyncDiagnosticStats {
      return {
         visibilityEventsFired: 0,
         visibilityPollsFired: 0,
         transitions: 0,
         firstDetectionWinsByEvent: 0,
         firstDetectionWinsByPoll: 0,
         probeAttempts: 0,
         probeAFailures: 0,
         probeBFailures: 0,
      };
   }

   private attachDebugHook(): void {
      // Mirrors __etsDebugDrift pattern at drift-detection.service.ts:336-349.
      // Present in all builds for support diagnostics.
      const self = this;
      (window as any).__etsDebugSessionSync = {
         get isSyncing(): boolean { return self._isSyncingFlag; },
         get overlayHookPresent(): boolean { return !!getOverlayHook(); },
         get overlayDomVisible(): boolean { return !!getOverlayHook()?.isVisible(); },
         get state(): SessionSyncState { return self._state; },
         get lastServerTimeMessage(): ServerTimeMessageDto | undefined { return self._lastServerTimeMessage; },
         get serverTimeGapMs(): number {
            if (!self._lastServerTimeMessage) {
               return -1;
            }
            const tsMs = SessionSyncOverlayService.toEpochMs(self._lastServerTimeMessage.time);
            return Number.isFinite(tsMs) ? Date.now() - tsMs : NaN;
         },
         get lastSpxOrEsQuoteArrivalAgeMs(): number {
            return self._lastSpxOrEsQuoteArrivalMs === 0
               ? -1
               : Date.now() - self._lastSpxOrEsQuoteArrivalMs;
         },
         get lastSpxOrEsQuoteTicker(): string { return self._lastSpxOrEsQuoteTicker; },
         get visibility(): { hidden: boolean; hasFocus: boolean; wasActive: boolean } {
            return {
               hidden: typeof document !== 'undefined' ? document.hidden : false,
               hasFocus: typeof document !== 'undefined' && typeof document.hasFocus === 'function'
                  ? document.hasFocus()
                  : true,
               wasActive: self._wasActive
            };
         },
         get stats(): SessionSyncDiagnosticStats { return Object.assign({}, self._stats); },
         forceShow(): void {
            self.setSyncing(true);
            console.info('[SessionSyncOverlayService] __etsDebugSessionSync.forceShow() — overlay forced ON');
         },
         forceHide(): void {
            self.setSyncing(false);
            console.info('[SessionSyncOverlayService] __etsDebugSessionSync.forceHide() — overlay forced OFF');
         },
         simulateHidden(): void {
            console.info('[SessionSyncOverlayService] __etsDebugSessionSync.simulateHidden() — synthesizing HIDDEN transition');
            self._wasActive = false;
            self.onHidden('init');
         },
         simulateVisible(): void {
            console.info('[SessionSyncOverlayService] __etsDebugSessionSync.simulateVisible() — synthesizing VISIBLE transition');
            self._wasActive = true;
            self.onVisible('init');
         },
      };
   }
}
